import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Input,
  OnDestroy,
  Type,
  ViewChild,
  inject,
  output,
} from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { Subscription } from 'rxjs';
import { Currency, TranslatePluralPipe } from '@wilson/localization';
import { LocalizedNumberPipe } from '@wilson/pipes';
import {
  AgreementFormControls,
  AgreementsFormService,
} from '../../../agreements-form.service';
import { AgreementRuleDirectiveDirective } from '../../directives/agreementRuleDirective.directive';
import { RuleDescriptionComponent } from '../partials/rule-description/rule-description.component';
import { RuleLabelComponent } from '../partials/rule-label/rule-label.component';
import { RULE_GROUP_ICON, RULE_TYPE_GROUP } from '../../rule-type-groups';
import {
  RULE_SUMMARY_COUNT_FIELD,
  RULE_SUMMARY_FIELD,
} from '../../rule-summary-field';
import { ActivityBonusByHourShiftAndActivityCategoryComponent } from '../activity-bonus-by-hour-shift-and-activity-category/activity-bonus-by-hour-shift-and-activity-category.component';
import { ActivityByCategoryAndHourRateComponent } from '../activity-by-category-and-hour-rate/activity-by-category-and-hour-rate.component';
import { ActivityByCategoryFlatRateComponent } from '../activity-by-category-flat-rate/activity-by-category-flat-rate.component';
import { ActivityByTonnageComponent } from '../activity-by-tonnage/activity-by-tonnage.component';
import { ActivityByWagonNumberComponent } from '../activity-by-wagon-number/activity-by-wagon-number.component';
import { ActivityFlatRateComponent } from '../activity-flat-rate/activity-flat-rate.component';
import { ActivityHourRateComponent } from '../activity-hour-rate/activity-hour-rate.component';
import { ActivityWithFixedMinimumCostComponent } from '../activity-with-fixed-minimum-cost/activity-with-fixed-minimum-cost.component';
import { AdHocShiftRateComponent } from '../ad-hoc-shift-rate/ad-hoc-shift-rate.component';
import { FixedShiftCategoryRateComponent } from '../fixed-shift-category-rate/fixed-shift-category-rate.component';
import { ShiftGradualDistanceAndDurationComponent } from '../shift-gradual-distance-and-duration/shift-gradual-distance-and-duration.component';
import { ShiftMinimumRateComponent } from '../shift-minimum-rate/shift-minimum-rate.component';
import { ShiftWithFixedMinimumCostComponent } from '../shift-with-fixed-minimum-cost/shift-with-fixed-minimum-cost.component';
import { TimeFrameBonusComponent } from '../time-frame-bonus/time-frame-bonus.component';
import { TimeFrameMinimumRateComponent } from '../time-frame-minimum-rate/time-frame-minimum-rate.component';
import { TimeFrameOvertimePremiumRateComponent } from '../time-frame-overtime-premium-rate/time-frame-overtime-premium-rate.component';
import { BillingRules, CalculatorName } from '@wilson/interfaces';

export interface UpdateRule {
  rule: BillingRules;
  index: number;
}

type RuleComponentTypes =
  | ActivityBonusByHourShiftAndActivityCategoryComponent
  | ActivityByCategoryAndHourRateComponent
  | ActivityByCategoryFlatRateComponent
  | ActivityByTonnageComponent
  | ActivityByWagonNumberComponent
  | ActivityFlatRateComponent
  | ActivityHourRateComponent
  | ActivityWithFixedMinimumCostComponent
  | AdHocShiftRateComponent
  | FixedShiftCategoryRateComponent
  | ShiftGradualDistanceAndDurationComponent
  | ShiftMinimumRateComponent
  | ShiftWithFixedMinimumCostComponent
  | TimeFrameBonusComponent
  | TimeFrameMinimumRateComponent
  | TimeFrameOvertimePremiumRateComponent;

// Loaded via dynamic import (not static top-level imports) so esbuild splits each rule editor
// into its own lazy chunk. These 16 components are otherwise identical in weight to whatever else
// this file statically imports, and static class references here would force all of them into
// every consumer's eager bundle, even ones that never render a billing rule (see WILP-12145).
const RULE_COMPONENT_LOADERS: Record<
  CalculatorName,
  () => Promise<Type<RuleComponentTypes>>
> = {
  ActivityBonusByHourShiftAndActivityCategory: () =>
    import(
      '../activity-bonus-by-hour-shift-and-activity-category/activity-bonus-by-hour-shift-and-activity-category.component'
    ).then((m) => m.ActivityBonusByHourShiftAndActivityCategoryComponent),
  ActivityByCategoryAndHourRate: () =>
    import(
      '../activity-by-category-and-hour-rate/activity-by-category-and-hour-rate.component'
    ).then((m) => m.ActivityByCategoryAndHourRateComponent),
  ActivityByCategoryFlatRate: () =>
    import(
      '../activity-by-category-flat-rate/activity-by-category-flat-rate.component'
    ).then((m) => m.ActivityByCategoryFlatRateComponent),
  ActivityByTonnage: () =>
    import('../activity-by-tonnage/activity-by-tonnage.component').then(
      (m) => m.ActivityByTonnageComponent,
    ),
  ActivityByWagonNumber: () =>
    import(
      '../activity-by-wagon-number/activity-by-wagon-number.component'
    ).then((m) => m.ActivityByWagonNumberComponent),
  ActivityFlatRate: () =>
    import('../activity-flat-rate/activity-flat-rate.component').then(
      (m) => m.ActivityFlatRateComponent,
    ),
  ActivityHourRate: () =>
    import('../activity-hour-rate/activity-hour-rate.component').then(
      (m) => m.ActivityHourRateComponent,
    ),
  ActivityWithFixedMinimumCost: () =>
    import(
      '../activity-with-fixed-minimum-cost/activity-with-fixed-minimum-cost.component'
    ).then((m) => m.ActivityWithFixedMinimumCostComponent),
  AdHocShiftRate: () =>
    import('../ad-hoc-shift-rate/ad-hoc-shift-rate.component').then(
      (m) => m.AdHocShiftRateComponent,
    ),
  FixedShiftCategoryRate: () =>
    import(
      '../fixed-shift-category-rate/fixed-shift-category-rate.component'
    ).then((m) => m.FixedShiftCategoryRateComponent),
  ShiftGradualDistanceAndDuration: () =>
    import(
      '../shift-gradual-distance-and-duration/shift-gradual-distance-and-duration.component'
    ).then((m) => m.ShiftGradualDistanceAndDurationComponent),
  ShiftMinimumRate: () =>
    import('../shift-minimum-rate/shift-minimum-rate.component').then(
      (m) => m.ShiftMinimumRateComponent,
    ),
  ShiftWithFixedMinimumCost: () =>
    import(
      '../shift-with-fixed-minimum-cost/shift-with-fixed-minimum-cost.component'
    ).then((m) => m.ShiftWithFixedMinimumCostComponent),
  TimeFrameBonus: () =>
    import('../time-frame-bonus/time-frame-bonus.component').then(
      (m) => m.TimeFrameBonusComponent,
    ),
  TimeFrameMinimumRate: () =>
    import('../time-frame-minimum-rate/time-frame-minimum-rate.component').then(
      (m) => m.TimeFrameMinimumRateComponent,
    ),
  TimeFrameOvertimePremiumRate: () =>
    import(
      '../time-frame-overtime-premium-rate/time-frame-overtime-premium-rate.component'
    ).then((m) => m.TimeFrameOvertimePremiumRateComponent),
};

type AgreementFormControlsWithRules = AgreementFormControls & {
  rules: FormControl<string | null>;
};

@Component({
  selector: 'wilson-generic-rule',
  imports: [
    TranslatePipe,
    TranslatePluralPipe,
    LocalizedNumberPipe,
    NzCollapseModule,
    NzIconModule,
    NzInputModule,
    FormsModule,
    AgreementRuleDirectiveDirective,
    RuleLabelComponent,
    RuleDescriptionComponent,
  ],
  templateUrl: './generic-rule.component.html',
  styleUrl: './generic-rule.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericRuleComponent implements AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly agreementFormService = inject(AgreementsFormService);
  private readonly translate = inject(TranslateService);

  @ViewChild(AgreementRuleDirectiveDirective, { static: false })
  wilsonAgreementRuleDirective!: AgreementRuleDirectiveDirective;
  @Input()
  rule!: BillingRules;
  @Input()
  index!: number;
  @Input()
  collapsed = true;

  update = output<BillingRules>();
  collapsedChange = output<boolean>();
  componentRef!: ComponentRef<RuleComponentTypes>;
  agreementRulesSubscription!: Subscription;
  updateRuleSubscription!: Subscription;

  #destroyed = false;

  ngAfterViewInit(): void {
    this.loadAgreementRuleSpecificComponent();
    this.initAgreementRulesSub();
  }

  ngOnDestroy(): void {
    this.#destroyed = true;
    if (this.updateRuleSubscription) {
      this.updateRuleSubscription.unsubscribe();
    }
    if (this.agreementRulesSubscription) {
      this.agreementRulesSubscription.unsubscribe();
    }
  }

  private initAgreementRulesSub() {
    if (
      (
        this.agreementFormService.form
          .controls as AgreementFormControlsWithRules
      )['rules']
    ) {
      this.agreementRulesSubscription = (
        this.agreementFormService.form
          .controls as AgreementFormControlsWithRules
      )['rules'].valueChanges.subscribe((val) => {
        if (val && typeof val === 'string') {
          const ruleOfInterest = JSON.parse(val as string)[this.index];
          if (!isEqual(ruleOfInterest, this.rule)) {
            this.rule = ruleOfInterest;
            if (this.componentRef) {
              this.componentRef.instance.rule = ruleOfInterest;
              const childCdr =
                this.componentRef.injector.get(ChangeDetectorRef);
              this.cdr.detectChanges();
              childCdr.detectChanges();
            }
          }
        }
      });
    }
  }

  private async loadAgreementRuleSpecificComponent(): Promise<void> {
    const viewContainerRef = this.wilsonAgreementRuleDirective.viewContainerRef;
    viewContainerRef.clear();

    const loadComponent = RULE_COMPONENT_LOADERS[this.rule.calculatorName];
    if (!loadComponent) {
      return;
    }

    const componentClass = await loadComponent();
    if (this.#destroyed) {
      return;
    }

    this.componentRef = viewContainerRef.createComponent(componentClass);
    this.componentRef.instance.rule = { ...this.rule };
    this.componentRef.instance.index = this.index;
    this.updateRuleSubscription =
      this.componentRef.instance.updateRule.subscribe((args: UpdateRule) =>
        this.updateRule(args),
      );
    this.cdr.detectChanges();
  }

  private updateRule(updateRule: UpdateRule) {
    const currentRulesString = (
      this.agreementFormService.form.controls as AgreementFormControlsWithRules
    )['rules']?.value as string;
    if (currentRulesString) {
      const currentRulesArray = JSON.parse(
        currentRulesString,
      ) as BillingRules[];
      if (currentRulesArray && currentRulesArray[updateRule.index]) {
        currentRulesArray[updateRule.index] = updateRule.rule;
        (
          this.agreementFormService.form
            .controls as AgreementFormControlsWithRules
        )['rules']?.patchValue(JSON.stringify(currentRulesArray));
        this.cdr.detectChanges();
      }
    }
    this.update.emit(updateRule.rule);
  }

  public setRuleName(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input?.value) {
      this.rule.ruleName = input.value;
      this.updateRule({
        rule: this.rule,
        index: this.index,
      });
    }
  }

  protected onActiveChange(active: boolean): void {
    this.collapsedChange.emit(!active);
  }

  protected groupIcon(): string {
    return RULE_GROUP_ICON[RULE_TYPE_GROUP[this.rule.calculatorName]];
  }

  // Headline value shown on a collapsed row, e.g. "Price per hour — €24.50". Returns null for
  // rule types with no single numeric field (see summaryCountSpec) or an unset value.
  protected summaryFieldSpec(): {
    label: string;
    value: number;
  } | null {
    const spec = RULE_SUMMARY_FIELD[this.rule.calculatorName];
    if (!spec) return null;
    const value = (this.rule.params as Record<string, unknown>)[spec.field];
    if (typeof value !== 'number') return null;
    return { label: this.translate.instant(spec.labelKey), value };
  }

  // Fallback for rule types whose core data is an array (bonuses/ranges) rather than one field.
  protected summaryCountSpec(): { i18nBase: string; count: number } | null {
    const spec = RULE_SUMMARY_COUNT_FIELD[this.rule.calculatorName];
    if (!spec) return null;
    const items = (this.rule.params as Record<string, unknown>)[spec.field];
    return {
      i18nBase: spec.i18nBase,
      count: Array.isArray(items) ? items.length : 0,
    };
  }

  protected currency(): Currency {
    return (
      (this.agreementFormService.form.get('currency')?.value as Currency) ??
      Currency.EUR
    );
  }
}
